import React, { useEffect, useState } from 'react';

import { Enum, States } from '@vestahealthcare/common/enums';
import { translate } from '@vestahealthcare/common/i18n';

import { Button, CollapsableSidebar, Select } from 'styleguide-v2';

type Props = {
  states?: States[];
  open: boolean;
  onClose: () => void;
  onSubmit: (states: string[]) => Promise<void>;
};

export const CompactStatesModal = ({
  states: statesParams,
  onClose,
  onSubmit,
  open,
}: Props) => {
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [loadingButton, setLoadingButton] = useState<boolean>(false);

  const [states, setStates] = useState<States[]>([]);

  useEffect(() => {
    if (open) {
      setSubmitted(false);
      setLoadingButton(false);
    }
  }, [open]);

  useEffect(() => {
    setStates(statesParams || []);
  }, [statesParams]);

  const doSubmit = async () => {
    setSubmitted(true);

    if (!states?.length) return;

    setLoadingButton(true);
    try {
      await onSubmit(states?.map(({ value }) => value));
      onClose();
      setLoadingButton(false);
    } catch (e) {
      setLoadingButton(false);
    }
  };

  return (
    <CollapsableSidebar
      title={<h2>{translate('employees.compactStatesConfig')}</h2>}
      onClose={onClose}
      open={open}
    >
      <CollapsableSidebar.Body>
        <Select
          data-cy="employees-modal-compact-states"
          disableClearable
          error={submitted && !states?.length}
          items={States.asArray.toSorted((a, b) =>
            a.toString().localeCompare(b.toString()),
          )}
          getItemLabel={Enum.toString}
          label={translate('employees.modal.state')}
          multiple
          onChange={setStates}
          value={states}
          required
        />
      </CollapsableSidebar.Body>
      <CollapsableSidebar.Buttons>
        <Button
          color="tertiary"
          data-cy="edit-compact-states-close"
          onClick={onClose}
        >
          {translate('global.close')}
        </Button>
        <Button
          color="secondary"
          data-cy="edit-compact-states-submit"
          loading={loadingButton}
          onClick={doSubmit}
        >
          {translate('global.save')}
        </Button>
      </CollapsableSidebar.Buttons>
    </CollapsableSidebar>
  );
};

export default CompactStatesModal;
